import React, { useEffect, useState } from 'react'
import { Form, Input, InputNumber, Modal, Select, Spin } from 'antd'
import { useRequest } from 'ahooks'
import $msg from '@/utils/message'
import { getMemberDetail, getShopPayment, refundMemberBalance } from '../api'

interface RechargeOrder {
    id: number
    recharge_amount: number
    recharge_bonus: number
    actually_paid?: number
    payment?: { payment_type?: number; payment_amount?: number; payment_status?: number }
}

interface Props {
    visible: boolean
    memberId: number
    rechargeOrder?: RechargeOrder
    onCancel: () => void
    onSuccess: () => void
}

interface RefundForm {
    refund_amount: number
    refund_bonus_amount: number
    refund_type: number
    refund_reason?: string
    custom_payment_id?: number
}

const onlinePaymentTypes = [2, 3, 5, 6, 7, 8, 13, 14, 15, 16, 17, 19, 20, 21, 22, 23, 24, 25]

const MemberBalanceRefundModal: React.FC<Props> = ({ visible, memberId, rechargeOrder, onCancel, onSuccess }) => {
    const [form] = Form.useForm<RefundForm>()
    const [submitting, setSubmitting] = useState(false)
    const memberReq = useRequest(() => getMemberDetail(memberId), { manual: true, onError: e => $msg('error', e.message) })
    const paymentReq = useRequest(getShopPayment, { manual: true, onError: e => $msg('error', e.message) })
    const loadMember = memberReq.run
    const loadPayment = paymentReq.run
    const rechargeOrderId = rechargeOrder?.id

    useEffect(() => {
        if (!visible) return
        form.resetFields()
        loadMember()
        loadPayment()
    }, [visible, memberId, rechargeOrderId, form, loadMember, loadPayment])

    const member = memberReq.data
    const availableBalance = Number(member?.balance || 0)
    const availableBonus = Number(member?.bonus || 0)
    const maxAmount = Math.min(availableBalance, Number(rechargeOrder?.recharge_amount ?? availableBalance))
    const maxBonus = Math.min(availableBonus, Number(rechargeOrder?.recharge_bonus ?? availableBonus))
    const payment = rechargeOrder?.payment
    const canRefundOnline = !!(
        rechargeOrder &&
        payment &&
        onlinePaymentTypes.includes(Number(payment.payment_type)) &&
        payment.payment_status === 1
    )
    const paymentOptions = [
        ...(canRefundOnline ? [{ value: 0, label: '原路退回' }] : []),
        { value: 1, label: '个人微信转账' },
        { value: 2, label: '个人支付宝转账' },
        { value: 3, label: '现金退款' },
        { value: 4, label: '银行卡转账' },
        ...(paymentReq.data?.custom_payment_config?.length ? [{ value: 6, label: '自定义退款方式' }] : []),
    ]
    const customPaymentOptions = (paymentReq.data?.custom_payment_config || [])
        .filter((item: { status?: number }) => item.status !== -1)
        .map((item: { id: number; name: string }) => ({ value: item.id, label: item.name }))

    const submit = async () => {
        if (submitting || memberReq.loading || !member) return
        const values = await form.validateFields()
        const amount = Number(values.refund_amount || 0)
        const bonus = Number(values.refund_bonus_amount || 0)
        if (amount < 0 || bonus < 0 || amount + bonus <= 0 || amount > maxAmount || bonus > maxBonus) {
            $msg('warning', '退款金额超出当前可退余额或充值单金额')

            return
        }
        if (values.refund_type === 0 && (!canRefundOnline || amount > Number(payment?.payment_amount || 0))) {
            $msg('warning', '原路退款金额不能超过原线上实付金额')

            return
        }
        Modal.confirm({
            title: '确认发起会员余额退款？',
            content: `退回余额 ${amount.toFixed(2)} 元、赠送金 ${bonus.toFixed(2)} 元。提交后请先查询退款记录，不要重复点击。`,
            okText: '确认退款',
            okButtonProps: { danger: true },
            onOk: async () => {
                setSubmitting(true)
                try {
                    await refundMemberBalance({
                        member_id: memberId,
                        recharge_order_id: rechargeOrder?.id,
                        refund_amount: amount,
                        refund_bonus_amount: bonus,
                        refund_type: values.refund_type,
                        refund_reason: values.refund_reason,
                        custom_payment_id: values.refund_type === 6 ? values.custom_payment_id : undefined,
                    })
                    $msg('success', '退款请求已提交，请核对退款记录')
                    onSuccess()
                } catch (error) {
                    $msg('error', `${error.message || '退款请求失败'}，请先核对记录，勿直接重试`)
                } finally {
                    setSubmitting(false)
                }
            },
        })
    }

    return (
        <Modal
            visible={visible}
            title={rechargeOrder ? '充值记录退款' : '会员余额退款'}
            okText="确认退款"
            okButtonProps={{ danger: true, loading: submitting, disabled: memberReq.loading || submitting }}
            onOk={submit}
            onCancel={onCancel}
            maskClosable={!submitting}
            closable={!submitting}
            destroyOnClose
            forceRender
        >
            <Spin spinning={memberReq.loading}>
                <p>当前余额：{availableBalance.toFixed(2)} 元，赠送金：{availableBonus.toFixed(2)} 元</p>
                {rechargeOrder ? <p>本笔充值可选金额：{maxAmount.toFixed(2)} 元，赠送金：{maxBonus.toFixed(2)} 元</p> : null}
                <Form form={form} layout="vertical" initialValues={{ refund_amount: 0, refund_bonus_amount: 0, refund_type: 3 }}>
                    <Form.Item name="refund_amount" label="退回余额" rules={[{ required: true, message: '请输入退款金额' }]}>
                        <InputNumber min={0} max={maxAmount} precision={2} style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item name="refund_bonus_amount" label="扣减赠送金" rules={[{ required: true, message: '请输入赠送金金额' }]}>
                        <InputNumber min={0} max={maxBonus} precision={2} style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item name="refund_type" label="退款方式" rules={[{ required: true, message: '请选择退款方式' }]}>
                        <Select options={paymentOptions} />
                    </Form.Item>
                    <Form.Item noStyle shouldUpdate={(prev, next) => prev.refund_type !== next.refund_type}>
                        {({ getFieldValue }) => getFieldValue('refund_type') === 6 ? (
                            <Form.Item name="custom_payment_id" label="自定义退款方式" rules={[{ required: true, message: '请选择退款方式' }]}>
                                <Select options={customPaymentOptions} />
                            </Form.Item>
                        ) : null}
                    </Form.Item>
                    <Form.Item name="refund_reason" label="退款原因">
                        <Input.TextArea maxLength={100} rows={2} />
                    </Form.Item>
                </Form>
            </Spin>
        </Modal>
    )
}

export default MemberBalanceRefundModal
